import React, { useState, useEffect } from 'react';
import {
  WeightGoal,
  Intensity,
  PlanDuration,
  NutritionPlanResult,
  NutritionPlannerMetadata,
} from '../../services/chatService';
import './NutritionPlanner.css';

interface NutritionPlannerProps {
  isOpen: boolean;
  onClose: () => void;
  onCreatePlan: (weightGoal: WeightGoal, intensity: Intensity, durationWeeks: PlanDuration) => Promise<NutritionPlanResult>;
  onDone: (result: NutritionPlanResult) => void;
  metadata: NutritionPlannerMetadata | null;
  isLoading: boolean;
}

export const NutritionPlanner: React.FC<NutritionPlannerProps> = ({
  isOpen,
  onClose,
  onCreatePlan,
  onDone,
  metadata,
  isLoading,
}) => {
  const [weightGoal, setWeightGoal] = useState<WeightGoal>('weight_loss');
  const [intensity, setIntensity] = useState<Intensity>('normal');
  const [durationWeeks, setDurationWeeks] = useState<PlanDuration>(6);
  const [result, setResult] = useState<NutritionPlanResult | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedWeeks, setExpandedWeeks] = useState<number[]>([1]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setWeightGoal('weight_loss');
      setIntensity('normal');
      setDurationWeeks(6);
      setResult(null);
      setError(null);
      setExpandedWeeks([1]);
    }
  }, [isOpen]);

  const handleCreatePlan = async () => {
    setAnalyzing(true);
    setError(null);

    try {
      const planResult = await onCreatePlan(weightGoal, intensity, durationWeeks);
      setResult(planResult);
    } catch (err: any) {
      setError(err.message || 'Failed to create nutrition plan. Please complete your profile first.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDone = () => {
    if (result) {
      onDone(result);
    }
    onClose();
  };

  const toggleWeek = (week: number) => {
    setExpandedWeeks(prev =>
      prev.includes(week)
        ? prev.filter(w => w !== week)
        : [...prev, week]
    );
  };

  const getGoalIcon = (goal: WeightGoal) => {
    switch (goal) {
      case 'weight_loss': return '📉';
      case 'weight_gain': return '📈';
      case 'maintain': return '⚖️';
    }
  };

  const getIntensityColor = (int: Intensity) => {
    switch (int) {
      case 'gentle': return '#38a169';
      case 'normal': return '#d69e2e';
      case 'aggressive': return '#e53e3e';
    }
  };

  const getWarningIcon = (level: string) => {
    switch (level) {
      case 'info': return 'ℹ️';
      case 'caution': return '⚠️';
      case 'warning': return '🚨';
      default: return '📋';
    }
  };

  const getBmiColor = (bmi: number) => {
    if (bmi < 18.5) return '#3182ce'; // Underweight - blue
    if (bmi < 25) return '#38a169'; // Normal - green
    if (bmi < 30) return '#d69e2e'; // Overweight - yellow
    return '#e53e3e'; // Obese - red
  };

  if (!isOpen) return null;

  return (
    <div className="nutrition-planner-overlay" onClick={onClose}>
      <div className="nutrition-planner-modal" onClick={e => e.stopPropagation()}>
        <div className="nutrition-planner-header">
          <h2>🍽️ Nutrition & Calorie Planner</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {!result ? (
          <>
            <div className="nutrition-planner-body">
              <p className="nutrition-intro">
                Create a personalized calorie plan based on your health profile. We'll calculate your maintenance calories and create a weekly plan.
              </p>

              {error && <div className="nutrition-error">{error}</div>}

              {isLoading ? (
                <div className="nutrition-loading">Loading options...</div>
              ) : (
                <div className="nutrition-options">
                  {/* Weight Goal Selection */}
                  <div className="option-group">
                    <label className="option-label">🎯 What's your goal?</label>
                    <div className="goal-options">
                      {(metadata?.weightGoals || [
                        { value: 'weight_loss' as WeightGoal, label: 'Weight Loss', description: 'Reduce body weight' },
                        { value: 'weight_gain' as WeightGoal, label: 'Weight Gain', description: 'Increase body weight' },
                        { value: 'maintain' as WeightGoal, label: 'Maintain', description: 'Keep current weight' },
                      ]).map(goal => (
                        <button
                          key={goal.value}
                          className={`goal-btn ${weightGoal === goal.value ? 'selected' : ''}`}
                          onClick={() => setWeightGoal(goal.value)}
                        >
                          <span className="goal-icon">{getGoalIcon(goal.value)}</span>
                          <span className="goal-label">{goal.label}</span>
                          <span className="goal-desc">{goal.description}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Intensity Selection */}
                  <div className="option-group">
                    <label className="option-label">⚡ Choose intensity</label>
                    <div className="intensity-options">
                      {(metadata?.intensityOptions || [
                        { value: 'gentle' as Intensity, label: 'Gentle', description: '±100 kcal/week', weeklyAdjustment: 100 },
                        { value: 'normal' as Intensity, label: 'Normal', description: '±150-200 kcal/week', weeklyAdjustment: 175 },
                        { value: 'aggressive' as Intensity, label: 'Aggressive', description: '±250-450 kcal/week', weeklyAdjustment: 350 },
                      ]).map(int => (
                        <button
                          key={int.value}
                          className={`intensity-btn ${intensity === int.value ? 'selected' : ''}`}
                          onClick={() => setIntensity(int.value)}
                          style={{ borderColor: intensity === int.value ? getIntensityColor(int.value) : undefined }}
                        >
                          <span className="intensity-label">{int.label}</span>
                          <span className="intensity-desc">{int.description}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Duration Selection */}
                  <div className="option-group">
                    <label className="option-label">📅 Plan duration</label>
                    <div className="duration-options">
                      {(metadata?.durationOptions || [
                        { weeks: 4, label: '4 Weeks', description: 'Short-term focus' },
                        { weeks: 6, label: '6 Weeks', description: 'Balanced duration' },
                        { weeks: 8, label: '8 Weeks', description: 'Extended plan' },
                      ]).map(dur => (
                        <button
                          key={dur.weeks}
                          className={`duration-btn ${durationWeeks === dur.weeks ? 'selected' : ''}`}
                          onClick={() => setDurationWeeks(dur.weeks as PlanDuration)}
                        >
                          <span className="duration-label">{dur.label}</span>
                          <span className="duration-desc">{dur.description}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="nutrition-planner-footer">
              <button className="cancel-btn" onClick={onClose}>Cancel</button>
              <button
                className="create-plan-btn"
                onClick={handleCreatePlan}
                disabled={analyzing || isLoading}
              >
                {analyzing ? 'Creating Plan...' : '🧮 Calculate My Plan'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="nutrition-planner-body results">
              {/* Profile Summary */}
              <div className="result-section profile-summary">
                <h3>👤 Your Profile</h3>
                <div className="profile-grid">
                  <div className="profile-item">
                    <span className="profile-label">Age</span>
                    <span className="profile-value">{result.profileSummary.age} years</span>
                  </div>
                  <div className="profile-item">
                    <span className="profile-label">Height</span>
                    <span className="profile-value">{result.profileSummary.height} cm</span>
                  </div>
                  <div className="profile-item">
                    <span className="profile-label">Weight</span>
                    <span className="profile-value">{result.profileSummary.weight} kg</span>
                  </div>
                  <div className="profile-item">
                    <span className="profile-label">BMI</span>
                    <span className="profile-value" style={{ color: getBmiColor(result.profileSummary.bmi) }}>
                      {result.profileSummary.bmi} ({result.profileSummary.bmiCategory})
                    </span>
                  </div>
                  <div className="profile-item">
                    <span className="profile-label">Activity</span>
                    <span className="profile-value">{result.profileSummary.activityLevel}</span>
                  </div>
                </div>
              </div>

              {/* Maintenance Calories */}
              <div className="result-section maintenance-section">
                <h3>🔥 Maintenance Calories</h3>
                <div className="maintenance-info">
                  <div className="calorie-display">
                    <span className="calorie-value">{result.maintenanceCalories.tdee}</span>
                    <span className="calorie-unit">kcal/day</span>
                  </div>
                  <div className="calorie-details">
                    <span>BMR: {result.maintenanceCalories.bmr} kcal</span>
                    <span>Activity Multiplier: ×{result.maintenanceCalories.activityMultiplier}</span>
                    <span>Formula: {result.maintenanceCalories.formula}</span>
                  </div>
                </div>
              </div>

              {/* Safety Warnings */}
              {result.safetyWarnings.length > 0 && (
                <div className="result-section warnings-section">
                  <h3>⚠️ Important Considerations</h3>
                  <div className="warnings-list">
                    {result.safetyWarnings.map((warning, idx) => (
                      <div key={idx} className={`warning-item ${warning.level}`}>
                        <span className="warning-icon">{getWarningIcon(warning.level)}</span>
                        <span className="warning-message">{warning.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pre-Condition Impacts */}
              {result.preConditionImpacts.length > 0 && (
                <div className="result-section conditions-section">
                  <h3>🩺 Condition Adjustments</h3>
                  <div className="conditions-list">
                    {result.preConditionImpacts.map((condition, idx) => (
                      <div key={idx} className="condition-item">
                        <div className="condition-header">
                          <span className="condition-name">{condition.condition}</span>
                          <span className="condition-code">ICD-10: {condition.icd10Code}</span>
                        </div>
                        <p className="condition-impact">{condition.impact}</p>
                        <p className="condition-diet">💡 {condition.dietaryRecommendation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Weekly Plan */}
              <div className="result-section weekly-plan-section">
                <h3>📊 Your {result.durationWeeks}-Week Plan</h3>
                <p className="plan-description">{result.intensityDescription}</p>
                <div className="weekly-plan">
                  {result.weeklyPlan.map(week => (
                    <div key={week.week} className="week-card">
                      <button
                        className={`week-header ${expandedWeeks.includes(week.week) ? 'expanded' : ''}`}
                        onClick={() => toggleWeek(week.week)}
                      >
                        <span className="week-number">Week {week.week}</span>
                        <span className="week-calories">{week.dailyCalories} kcal/day</span>
                        <span className="week-change" style={{ color: week.adjustmentFromMaintenance < 0 ? '#e53e3e' : week.adjustmentFromMaintenance > 0 ? '#38a169' : '#718096' }}>
                          {week.adjustmentFromMaintenance >= 0 ? '+' : ''}{week.adjustmentFromMaintenance} kcal
                        </span>
                        <span className="expand-icon">{expandedWeeks.includes(week.week) ? '−' : '+'}</span>
                      </button>
                      {expandedWeeks.includes(week.week) && (
                        <div className="week-details">
                          <div className="macros-grid">
                            <div className="macro-item protein">
                              <span className="macro-label">Protein</span>
                              <span className="macro-value">{week.macros.proteinG}g</span>
                            </div>
                            <div className="macro-item carbs">
                              <span className="macro-label">Carbs</span>
                              <span className="macro-value">{week.macros.carbsG}g</span>
                            </div>
                            <div className="macro-item fat">
                              <span className="macro-label">Fat</span>
                              <span className="macro-value">{week.macros.fatG}g</span>
                            </div>
                          </div>
                          <div className="weight-prediction">
                            Expected weight change: <strong>{week.expectedWeightChange >= 0 ? '+' : ''}{week.expectedWeightChange} kg</strong>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Expected Outcome */}
              <div className="result-section outcome-section">
                <h3>🎯 Expected Outcome</h3>
                <div className="outcome-grid">
                  <div className="outcome-item">
                    <span className="outcome-label">Starting Weight</span>
                    <span className="outcome-value">{result.profileSummary.weight} kg</span>
                  </div>
                  <div className="outcome-item">
                    <span className="outcome-label">Expected Final Weight</span>
                    <span className="outcome-value">{result.expectedOutcome.expectedFinalWeight} kg</span>
                  </div>
                  <div className="outcome-item">
                    <span className="outcome-label">Expected Change</span>
                    <span className="outcome-value" style={{ color: result.expectedOutcome.expectedWeightChange < 0 ? '#e53e3e' : '#38a169' }}>
                      {result.expectedOutcome.expectedWeightChange >= 0 ? '+' : ''}{result.expectedOutcome.expectedWeightChange} kg
                    </span>
                  </div>
                  <div className="outcome-item">
                    <span className="outcome-label">Final Daily Calories</span>
                    <span className="outcome-value">{result.expectedOutcome.finalCalories} kcal</span>
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="result-disclaimer">
                <p>{result.disclaimer}</p>
              </div>
            </div>

            <div className="nutrition-planner-footer">
              <button className="back-btn" onClick={() => setResult(null)}>← Back</button>
              <button className="done-btn" onClick={handleDone}>
                ✓ Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
